// File: src/pages/AboutUs.jsx
import React from 'react';
import { Container, Typography, Grid, Box, Paper, List, ListItem, ListItemIcon, ListItemText } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PhoneIcon from '@mui/icons-material/Phone';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import LanguageIcon from '@mui/icons-material/Language';

const AboutUs = () => {
  React.useEffect(() => {
    // Scroll to top when component mounts
    window.scrollTo(0, 0);
    return () => { };
  }, []);

  return (
    <Box sx={{ backgroundColor: '#fbfbfb', minHeight: '100vh' }}>
      {/* Header Section */}
      <Box
        sx={{
          backgroundColor: '#0a233b',
          color: '#fbfbfb',
          py: 6,
          position: 'relative',
          overflow: 'hidden'
        }}
      >
        <Container maxWidth="lg">
          <Typography
            variant="h2"
            component="h1"
            sx={{
              fontWeight: 'bold',
              mb: 2,
              position: 'relative',
              zIndex: 2,
              textAlign: 'center'
            }}
          >
            ABOUT US
          </Typography>
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              right: 0,
              width: '40%',
              height: '100%',
              background: 'url(https://source.unsplash.com/random/800x600/?property,building) center/cover no-repeat',
              opacity: 0.2,
              zIndex: 1
            }}
          />
        </Container>
      </Box>

      {/* Who We Are Section */}
      <Container maxWidth="lg" sx={{ py: 6 }}>
        <Grid container spacing={4}>
          <Grid item xs={12} md={6}>
            <Typography variant="h4" component="h2" sx={{ color: '#0a233b', fontWeight: 'bold', mb: 3 }}>
              Who We Are
            </Typography>
            <Typography variant="body1" sx={{ color: '#6a6565', mb: 3, lineHeight: 1.8 }}>
              Lantena Property Managers is a premier property and project management firm dedicated to providing seamless, technology-driven, and client-focused solutions. With a deep understanding of the Kenyan real estate market, we offer a full spectrum of services, ensuring landlords, property owners, and investors enjoy hassle-free asset management with maximized returns.
            </Typography>
            <Typography variant="body1" sx={{ color: '#6a6565', mb: 3, lineHeight: 1.8 }}>
              We specialize in property management, project management, luxury renovations, and MRI tax support, ensuring compliance with Kenyan laws and KRA regulations while maintaining the highest standards of service.
            </Typography>
          </Grid>
          <Grid item xs={12} md={6}>
            <Box
              sx={{
                backgroundColor: '#a6d1ea',
                p: 4,
                borderRadius: 2,
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center'
              }}
            >
              <Typography variant="h4" component="h2" sx={{ color: '#0a233b', fontWeight: 'bold', mb: 3 }}>
                Our Mission
              </Typography>
              <Typography variant="body1" sx={{ color: '#0a233b', fontWeight: 'medium', fontStyle: 'italic', lineHeight: 1.8 }}>
                To revolutionize property and project management by providing efficient, transparent, and innovative solutions that enhance value, optimize investments, and guarantee peace of mind for our clients.
              </Typography>
            </Box>
          </Grid>
        </Grid>
      </Container>

      {/* What We Do Section */}
      <Box sx={{ backgroundColor: '#0a233b', color: '#fbfbfb', py: 6 }}>
        <Container maxWidth="lg">
          <Typography variant="h4" component="h2" sx={{ fontWeight: 'bold', mb: 4, textAlign: 'center' }}>
            What We Do
          </Typography>

          <Grid container spacing={4}>
            <Grid item xs={12} md={6} lg={3}>
              <Paper elevation={0} sx={{ p: 3, height: '100%', backgroundColor: 'rgba(166, 209, 234, 0.1)', borderTop: '4px solid #f66b10' }}>
                <Typography variant="h5" component="h3" sx={{ color: '#a6d1ea', fontWeight: 'bold', mb: 2 }}>
                  1. Property Management
                </Typography>
                <Typography variant="body2" sx={{ color: '#fbfbfb', lineHeight: 1.7 }}>
                  We provide full-service property management, ensuring timely rent collection, maintenance coordination, tenant screening, and legal compliance. Our cutting-edge management software allows landlords to monitor everything in real-time, from tenant payments and balances to maintenance requests. Rent is collected directly into the landlord\'s account, ensuring full financial transparency and security.
                </Typography>
              </Paper>
            </Grid>

            <Grid item xs={12} md={6} lg={3}>
              <Paper elevation={0} sx={{ p: 3, height: '100%', backgroundColor: 'rgba(166, 209, 234, 0.1)', borderTop: '4px solid #f66b10' }}>
                <Typography variant="h5" component="h3" sx={{ color: '#a6d1ea', fontWeight: 'bold', mb: 2 }}>
                  2. Project Management
                </Typography>
                <Typography variant="body2" sx={{ color: '#fbfbfb', lineHeight: 1.7 }}>
                  From concept to completion, we manage real estate development projects, ensuring timely execution, cost efficiency, and quality delivery. Whether it's constructing a new property or overseeing large-scale renovations, we bring expertise and professionalism to every project.
                </Typography>
              </Paper>
            </Grid>

            <Grid item xs={12} md={6} lg={3}>
              <Paper elevation={0} sx={{ p: 3, height: '100%', backgroundColor: 'rgba(166, 209, 234, 0.1)', borderTop: '4px solid #f66b10' }}>
                <Typography variant="h5" component="h3" sx={{ color: '#a6d1ea', fontWeight: 'bold', mb: 2 }}>
                  3. Luxury Renovations
                </Typography>
                <Typography variant="body2" sx={{ color: '#fbfbfb', lineHeight: 1.7 }}>
                  Transform your property with premium upgrades and high-end finishing. Our renovation team ensures that your spaces reflect modern aesthetics, functionality, and long-term value appreciation.
                </Typography>
              </Paper>
            </Grid>

            <Grid item xs={12} md={6} lg={3}>
              <Paper elevation={0} sx={{ p: 3, height: '100%', backgroundColor: 'rgba(166, 209, 234, 0.1)', borderTop: '4px solid #f66b10' }}>
                <Typography variant="h5" component="h3" sx={{ color: '#a6d1ea', fontWeight: 'bold', mb: 2 }}>
                  4. MRI Tax Support
                </Typography>
                <Typography variant="body2" sx={{ color: '#fbfbfb', lineHeight: 1.7 }}>
                  We help property owners navigate the complex landscape of rental income tax compliance by providing comprehensive MRI tax services, ensuring adherence to KRA regulations and reducing tax liabilities.
                </Typography>
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Why Choose Us Section */}
      <Container maxWidth="lg" sx={{ py: 6 }}>
        <Typography variant="h4" component="h2" sx={{ color: '#0a233b', fontWeight: 'bold', mb: 4, textAlign: 'center' }}>
          Why Choose Lantena?
        </Typography>

        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <List>
              {[
                'Tech-Driven Management – Access real-time property updates and reports via our innovative management platform.',
                'Full Financial Transparency – Rent is collected directly into the landlord\'s account, ensuring complete control over your finances.',
                'Compliance & Legal Expertise – Avoid penalties with our expert KRA tax filing and legal advisory services.'
              ].map((text, index) => (
                <ListItem key={index} alignItems="flex-start">
                  <ListItemIcon>
                    <CheckCircleIcon sx={{ color: '#f66b10' }} />
                  </ListItemIcon>
                  <ListItemText
                    primary={text}
                    primaryTypographyProps={{
                      variant: 'body1',
                      color: '#6a6565',
                      fontWeight: 'medium'
                    }}
                  />
                </ListItem>
              ))}
            </List>
          </Grid>
          <Grid item xs={12} md={6}>
            <List>
              {[
                'Tailored Solutions – Whether you own a few units or a large portfolio, we provide customized services to fit your needs.',
                'Trusted & Transparent – We believe in building lasting relationships through integrity, professionalism, and accountability.',
                'Maximized Returns – Our strategic approach ensures higher occupancy rates, lower maintenance costs, and optimized tax efficiencies.'
              ].map((text, index) => (
                <ListItem key={index} alignItems="flex-start">
                  <ListItemIcon>
                    <CheckCircleIcon sx={{ color: '#f66b10' }} />
                  </ListItemIcon>
                  <ListItemText
                    primary={text}
                    primaryTypographyProps={{
                      variant: 'body1',
                      color: '#6a6565',
                      fontWeight: 'medium'
                    }}
                  />
                </ListItem>
              ))}
            </List>
          </Grid>
        </Grid>
      </Container>

      {/* Call to Action Section */}
      <Box sx={{ backgroundColor: '#a6d1ea', py: 6 }}>
        <Container maxWidth="md" sx={{ textAlign: 'center' }}>
          <Typography variant="h4" component="h2" sx={{ color: '#0a233b', fontWeight: 'bold', mb: 3 }}>
            Let's Elevate Your Real Estate Experience
          </Typography>
          <Typography variant="body1" sx={{ color: '#0a233b', mb: 4, lineHeight: 1.8 }}>
            At Lantena, we don't just manage properties—we revolutionize the experience. Whether you're a local investor or based abroad, we ensure your real estate assets are in the best hands.
          </Typography>

          <Grid container spacing={3} justifyContent="center">
            <Grid item xs={12} sm={4}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <PhoneIcon sx={{ color: '#0a233b', mr: 1 }} />
                <Typography variant="body1" sx={{ color: '#0a233b', fontWeight: 'medium' }}>
                  0722575313 / 0736261808
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <LocationOnIcon sx={{ color: '#0a233b', mr: 1 }} />
                <Typography variant="body1" sx={{ color: '#0a233b', fontWeight: 'medium' }}>
                  Ouru Complex, 3rd floor, Kisii
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <LanguageIcon sx={{ color: '#0a233b', mr: 1 }} />
                <Typography variant="body1" sx={{ color: '#0a233b', fontWeight: 'medium' }}>
                  www.lantenapm.com
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Final Tagline */}
      <Box sx={{ backgroundColor: '#0a233b', color: '#fbfbfb', py: 3, textAlign: 'center' }}>
        <Container>
          <Typography variant="h5" component="p" sx={{ fontWeight: 'medium', fontStyle: 'italic' }}>
            Lantena Property Managers – Your Trusted, Accountable and Transparent Property and Project Partner.
          </Typography>
        </Container>
      </Box>
    </Box>
  );
};

export default AboutUs;
